// src/components/dashboard/BookingAnalytics.js
import {
  ArrowDown,
  ArrowUp,
  BarChart3,
  Calendar,
  DollarSign,
  Download,
  Filter,
  Star,
  Users
} from 'lucide-react';
import { useMemo, useState } from 'react';
import { useAuth } from '../../hooks/useAuth';
import { useBookings } from '../../hooks/useBookings';

const BookingAnalytics = () => {
  const { user } = useAuth();
  const { bookings, loading } = useBookings();
  
  const [timeframe, setTimeframe] = useState('month');
  const [selectedMetric, setSelectedMetric] = useState('bookings');
  const [dateRange, setDateRange] = useState({ start: '', end: '' });

  // Calculate analytics data
  const analyticsData = useMemo(() => {
    if (!bookings.length) return null;

    const now = new Date();
    let startDate, endDate;

    // Set date range based on timeframe
    switch (timeframe) {
      case 'week':
        startDate = new Date(now.getFullYear(), now.getMonth(), now.getDate() - 7);
        endDate = now;
        break;
      case 'month':
        startDate = new Date(now.getFullYear(), now.getMonth(), 1);
        endDate = new Date(now.getFullYear(), now.getMonth() + 1, 0);
        break;
      case 'quarter':
        const quarter = Math.floor(now.getMonth() / 3);
        startDate = new Date(now.getFullYear(), quarter * 3, 1);
        endDate = new Date(now.getFullYear(), quarter * 3 + 3, 0);
        break;
      case 'year':
        startDate = new Date(now.getFullYear(), 0, 1);
        endDate = new Date(now.getFullYear(), 11, 31);
        break;
      case 'custom':
        startDate = dateRange.start ? new Date(dateRange.start) : new Date(now.getFullYear(), now.getMonth(), 1);
        endDate = dateRange.end ? new Date(dateRange.end) : now;
        break;
      default:
        startDate = new Date(now.getFullYear(), now.getMonth(), 1);
        endDate = new Date(now.getFullYear(), now.getMonth() + 1, 0);
    }

    // Filter bookings by date range
    const filteredBookings = bookings.filter(booking => {
      const bookingDate = new Date(booking.date || booking.booking_date || booking['bookingDate']);
      return bookingDate >= startDate && bookingDate <= endDate;
    });

    // Calculate metrics
    const confirmedBookings = filteredBookings.filter(b => b.status === 'confirmed');
    const rescheduledBookings = filteredBookings.filter(b => b.status === 'rescheduled');
    const cancelledBookings = filteredBookings.filter(b => b.status === 'cancelled');
    const validBookings = filteredBookings.filter(b => b.status === 'confirmed' || b.status === 'rescheduled');

    const totalRevenue = validBookings.reduce((sum, b) => sum + (b.price || b['servicePrice'] || 0), 0);
    const uniqueCustomers = new Set(filteredBookings.map(b => b.customer_whatsapp || b['customerId'] || b.customerPhone || b.id)).size;

    // Calculate trends (simplified - comparing to previous period)
    const previousPeriodStart = new Date(startDate);
    previousPeriodStart.setDate(startDate.getDate() - (endDate.getDate() - startDate.getDate()));
    const previousBookings = bookings.filter(booking => {
      const bookingDate = new Date(booking.date || booking.booking_date || booking['bookingDate']);
      return bookingDate >= previousPeriodStart && bookingDate < startDate;
    });

    const trends = {
      bookings: calculateTrend(filteredBookings.length, previousBookings.length),
      revenue: calculateTrend(totalRevenue, previousBookings.reduce((sum, b) => sum + (b.price || b['servicePrice'] || 0), 0)),
      customers: calculateTrend(uniqueCustomers, new Set(previousBookings.map(b => b.customer_whatsapp || b['customerId'] || b.customerPhone || b.id)).size)
    };

    return {
      period: { startDate, endDate },
      summary: {
        totalBookings: filteredBookings.length,
        confirmedBookings: confirmedBookings.length,
        rescheduledBookings: rescheduledBookings.length,
        cancelledBookings: cancelledBookings.length,
        totalRevenue,
        averageBookingValue: validBookings.length > 0 ? totalRevenue / validBookings.length : 0,
        uniqueCustomers,
        confirmationRate: filteredBookings.length > 0 ? (confirmedBookings.length / filteredBookings.length) * 100 : 0,
        cancellationRate: filteredBookings.length > 0 ? (cancelledBookings.length / filteredBookings.length) * 100 : 0
      },
      trends,
      serviceBreakdown: calculateServiceBreakdown(validBookings),
      dailyData: calculateDailyData(filteredBookings, startDate, endDate),
      customerInsights: calculateCustomerInsights(filteredBookings),
      timeAnalysis: calculateTimeAnalysis(validBookings)
    };
  }, [bookings, timeframe, dateRange]);

  const exportData = () => {
    if (!analyticsData) return;

    const csvData = [
      ['Metric', 'Value'],
      ['Total Bookings', analyticsData.summary.totalBookings],
      ['Confirmed Bookings', analyticsData.summary.confirmedBookings],
      ['Total Revenue', `$${analyticsData.summary.totalRevenue.toFixed(2)}`],
      ['Average Booking Value', `$${analyticsData.summary.averageBookingValue.toFixed(2)}`],
      ['Unique Customers', analyticsData.summary.uniqueCustomers],
      ['Confirmation Rate', `${analyticsData.summary.confirmationRate.toFixed(1)}%`],
      ['Cancellation Rate', `${analyticsData.summary.cancellationRate.toFixed(1)}%`]
    ];

    const csvContent = csvData.map(row => row.join(',')).join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `booking-analytics-${timeframe}-${new Date().toISOString().split('T')[0]}.csv`;
    link.click();
    window.URL.revokeObjectURL(url);
  };

  if (loading) {
    return (
      <div className="p-6">
        <div className="animate-pulse">
          <div className="h-8 bg-gray-200 rounded w-1/4 mb-6"></div>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-6">
            {[1, 2, 3, 4].map(i => (
              <div key={i} className="h-24 bg-gray-200 rounded"></div>
            ))}
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div className="h-64 bg-gray-200 rounded"></div>
            <div className="h-64 bg-gray-200 rounded"></div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Booking Analytics</h1>
          <p className="text-gray-600">Insights into your business performance</p>
        </div>
        
        <div className="flex items-center space-x-3">
          <button
            onClick={exportData}
            className="flex items-center px-4 py-2 text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50"
          >
            <Download className="h-4 w-4 mr-2" />
            Export
          </button>
        </div>
      </div>

      {/* Controls */}
      <div className="bg-white rounded-lg shadow-sm border p-4">
        <div className="flex flex-wrap gap-4 items-center">
          <div className="flex items-center space-x-2">
            <Filter className="h-4 w-4 text-gray-400" />
            <select
              value={timeframe}
              onChange={(e) => setTimeframe(e.target.value)}
              className="border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="week">Last 7 Days</option>
              <option value="month">This Month</option>
              <option value="quarter">This Quarter</option>
              <option value="year">This Year</option>
              <option value="custom">Custom Range</option>
            </select>
          </div>

          {timeframe === 'custom' && (
            <div className="flex items-center space-x-2">
              <input
                type="date"
                value={dateRange.start}
                onChange={(e) => setDateRange(prev => ({ ...prev, start: e.target.value }))}
                className="border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
              <span className="text-gray-500">to</span>
              <input
                type="date"
                value={dateRange.end}
                onChange={(e) => setDateRange(prev => ({ ...prev, end: e.target.value }))}
                className="border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
          )}

          {analyticsData && (
            <div className="text-sm text-gray-600">
              {analyticsData.period.startDate.toLocaleDateString()} - {analyticsData.period.endDate.toLocaleDateString()}
            </div>
          )}
        </div>
      </div>

      {analyticsData ? (
        <>
          {/* Key Metrics */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            <MetricCard
              title="Total Bookings"
              value={analyticsData.summary.totalBookings}
              trend={analyticsData.trends.bookings}
              icon={Calendar}
              color="blue"
            />
            
            <MetricCard
              title="Total Revenue"
              value={`$${analyticsData.summary.totalRevenue.toFixed(2)}`}
              trend={analyticsData.trends.revenue}
              icon={DollarSign}
              color="green"
            />
            
            <MetricCard
              title="Unique Customers"
              value={analyticsData.summary.uniqueCustomers}
              trend={analyticsData.trends.customers}
              icon={Users}
              color="purple"
            />
            
            <MetricCard
              title="Confirmation Rate"
              value={`${analyticsData.summary.confirmationRate.toFixed(1)}%`}
              trend={{ value: 0, direction: 'neutral' }}
              icon={Star}
              color="indigo"
            />
          </div>

          {/* Charts and Breakdowns */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Service Breakdown */}
            <div className="bg-white rounded-lg shadow-sm border p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Top Services</h3>
              <div className="space-y-4">
                {analyticsData.serviceBreakdown.slice(0, 5).map((service, index) => (
                  <div key={service.name} className="flex items-center justify-between">
                    <div className="flex items-center">
                      <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center mr-3">
                        <span className="text-sm font-medium text-blue-600">{index + 1}</span>
                      </div>
                      <div>
                        <p className="font-medium text-gray-900">{service.name}</p>
                        <p className="text-sm text-gray-600">{service.count} bookings</p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="font-medium text-gray-900">${service.revenue.toFixed(2)}</p>
                      <p className="text-sm text-gray-600">
                        {((service.count / analyticsData.summary.totalBookings) * 100).toFixed(1)}%
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Booking Status Distribution */}
            <div className="bg-white rounded-lg shadow-sm border p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Booking Status</h3>
              <div className="space-y-4">
                <StatusBar
                  label="Confirmed"
                  count={analyticsData.summary.confirmedBookings}
                  total={analyticsData.summary.totalBookings}
                  color="green"
                />
                <StatusBar
                  label="Rescheduled"
                  count={analyticsData.summary.rescheduledBookings}
                  total={analyticsData.summary.totalBookings}
                  color="blue"
                />
                <StatusBar
                  label="Cancelled"
                  count={analyticsData.summary.cancelledBookings}
                  total={analyticsData.summary.totalBookings}
                  color="red"
                />
              </div>
            </div>
          </div>

          {/* Additional Insights */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Customer Insights */}
            <div className="bg-white rounded-lg shadow-sm border p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Customer Insights</h3>
              <div className="space-y-3">
                <InsightRow
                  label="New Customers"
                  value={analyticsData.customerInsights.newCustomers}
                />
                <InsightRow
                  label="Repeat Customers"
                  value={analyticsData.customerInsights.repeatCustomers}
                />
                <InsightRow
                  label="Average Bookings per Customer"
                  value={analyticsData.customerInsights.avgBookingsPerCustomer.toFixed(1)}
                />
              </div>
            </div>

            {/* Time Analysis */}
            <div className="bg-white rounded-lg shadow-sm border p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Popular Times</h3>
              <div className="space-y-3">
                {analyticsData.timeAnalysis.popularHours.slice(0, 3).map((hour, index) => (
                  <div key={hour.hour} className="flex justify-between items-center">
                    <span className="text-gray-600">{formatHour(hour.hour)}</span>
                    <span className="font-medium">{hour.count} bookings</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Performance Summary */}
            <div className="bg-white rounded-lg shadow-sm border p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Performance</h3>
              <div className="space-y-3">
                <InsightRow
                  label="Average Booking Value"
                  value={`$${analyticsData.summary.averageBookingValue.toFixed(2)}`}
                />
                <InsightRow
                  label="Cancellation Rate"
                  value={`${analyticsData.summary.cancellationRate.toFixed(1)}%`}
                />
                <InsightRow
                  label="Revenue per Customer"
                  value={`$${(analyticsData.summary.totalRevenue / analyticsData.summary.uniqueCustomers).toFixed(2)}`}
                />
              </div>
            </div>
          </div>
        </>
      ) : (
        <div className="bg-white rounded-lg shadow-sm border p-8 text-center">
          <BarChart3 className="h-12 w-12 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">No booking data available</h3>
          <p className="text-gray-600">Start taking bookings to see your analytics here</p>
        </div>
      )}
    </div>
  );
};

// Component helpers
const MetricCard = ({ title, value, trend, icon: Icon, color }) => {
  const colorClasses = {
    blue: 'text-blue-600 bg-blue-100',
    green: 'text-green-600 bg-green-100',
    purple: 'text-purple-600 bg-purple-100',
    indigo: 'text-indigo-600 bg-indigo-100'
  };

  return (
    <div className="bg-white p-6 rounded-lg border shadow-sm">
      <div className="flex items-center justify-between">
        <div className="flex items-center">
          <div className={`p-2 rounded-lg ${colorClasses[color]}`}>
            <Icon className="h-6 w-6" />
          </div>
          <div className="ml-4">
            <p className="text-sm font-medium text-gray-600">{title}</p>
            <p className="text-2xl font-bold text-gray-900">{value}</p>
          </div>
        </div>
        
        {trend.direction !== 'neutral' && (
          <div className={`flex items-center ${
            trend.direction === 'up' ? 'text-green-600' : 'text-red-600'
          }`}>
            {trend.direction === 'up' ? (
              <ArrowUp className="h-4 w-4" />
            ) : (
              <ArrowDown className="h-4 w-4" />
            )}
            <span className="text-sm font-medium ml-1">{Math.abs(trend.value).toFixed(1)}%</span>
          </div>
        )}
      </div>
    </div>
  );
};

const StatusBar = ({ label, count, total, color }) => {
  const percentage = total > 0 ? (count / total) * 100 : 0;
  
  const colorClasses = {
    green: 'bg-green-500',
    blue: 'bg-blue-500',
    red: 'bg-red-500'
  };

  return (
    <div>
      <div className="flex justify-between items-center mb-2">
        <span className="text-sm font-medium text-gray-700">{label}</span>
        <span className="text-sm text-gray-600">{count} ({percentage.toFixed(1)}%)</span>
      </div>
      <div className="w-full bg-gray-200 rounded-full h-2">
        <div
          className={`h-2 rounded-full ${colorClasses[color]}`}
          style={{ width: `${percentage}%` }}
        />
      </div>
    </div>
  );
};

const InsightRow = ({ label, value }) => (
  <div className="flex justify-between items-center">
    <span className="text-sm text-gray-600">{label}</span>
    <span className="font-medium text-gray-900">{value}</span>
  </div>
);

// Helper functions
function calculateTrend(current, previous) {
  if (previous === 0) return { value: 0, direction: 'neutral' };
  const change = ((current - previous) / previous) * 100;
  return {
    value: change,
    direction: change > 0 ? 'up' : change < 0 ? 'down' : 'neutral'
  };
}

function calculateServiceBreakdown(bookings) {
  const services = {};
  
  bookings.forEach(booking => {
    const serviceName = booking.service_type || booking['serviceName'] || booking.service_name || 'Unknown';
    if (!services[serviceName]) {
      services[serviceName] = { name: serviceName, count: 0, revenue: 0 };
    }
    services[serviceName].count++;
    services[serviceName].revenue += booking.price || booking['servicePrice'] || 0;
  });
  
  return Object.values(services).sort((a, b) => b.count - a.count);
}

function calculateDailyData(bookings, startDate, endDate) {
  const days = {};
  const currentDate = new Date(startDate);
  
  // Initialize all days in range
  while (currentDate <= endDate) {
    const dateStr = currentDate.toISOString().split('T')[0];
    days[dateStr] = { date: dateStr, bookings: 0, revenue: 0 };
    currentDate.setDate(currentDate.getDate() + 1);
  }
  
  // Populate with booking data
  bookings.forEach(booking => {
    const dateStr = booking.date || booking.booking_date || booking['bookingDate'];
    if (days[dateStr]) {
      days[dateStr].bookings++;
      if (booking.status === 'confirmed' || booking.status === 'rescheduled') {
        days[dateStr].revenue += booking.price || booking['servicePrice'] || 0;
      }
    }
  });
  
  return Object.values(days);
}

function calculateCustomerInsights(bookings) {
  const customers = {};
  
  bookings.forEach(booking => {
    const customerId = booking.customer_whatsapp || booking['customerId'] || booking.customerPhone || booking.id;
    if (!customers[customerId]) {
      customers[customerId] = 0;
    }
    customers[customerId]++;
  });
  
  const customerCounts = Object.values(customers);
  const newCustomers = customerCounts.filter(count => count === 1).length;
  const repeatCustomers = customerCounts.filter(count => count > 1).length;
  
  return {
    newCustomers,
    repeatCustomers,
    avgBookingsPerCustomer: customerCounts.length > 0 
      ? customerCounts.reduce((sum, count) => sum + count, 0) / customerCounts.length 
      : 0
  };
}

function calculateTimeAnalysis(bookings) {
  const hours = {};
  
  bookings.forEach(booking => {
    if (booking.time) {
      const hour = parseInt(booking.time.split(':')[0]);
      hours[hour] = (hours[hour] || 0) + 1;
    }
  });
  
  const popularHours = Object.entries(hours)
    .map(([hour, count]) => ({ hour: parseInt(hour), count }))
    .sort((a, b) => b.count - a.count);
  
  return { popularHours };
}

function formatHour(hour) {
  const ampm = hour >= 12 ? 'PM' : 'AM';
  const displayHour = hour % 12 || 12;
  return `${displayHour}:00 ${ampm}`;
}

export default BookingAnalytics;
